import { useMemo, useState } from "react";

import { Box, ScrollArea, Space } from "@mantine/core";

import { CollectionNft } from "apollo/graphql.generated";
import {
  CollectionDetailsQuery,
  CollectionNftsQuery,
  useCollectionNftQuery,
  useCollectionNftsQuery,
} from "~/features/gates/queries/gates.graphql.generated";

import SectionFilterResults from "../condition-modals/sections/SectionFilterResults";

import {
  SimpleInfiniteGrid,
  SimpleInfiniteListItem,
} from "./SimpleInfiniteGrid";

type CollectionNfts = CollectionNftsQuery["collectionNftsPaginated"]["nfts"];

export default function OpenSeaCollectionTokenGrid(props: {
  openSeaCollection: CollectionDetailsQuery["collectionDetails"];
  selectedToken?: SimpleInfiniteListItem<CollectionNft>;
  onSelectToken: (tokenDetails: SimpleInfiniteListItem<CollectionNft>) => void;
  initialTokenId?: string;
}) {
  const { openSeaCollection, onSelectToken, selectedToken, initialTokenId } =
    props;
  const [searchToken, setSearchToken] = useState<string>(initialTokenId ?? "");

  const {
    openSeaCollectionNfts,
    loadMore,
    hasMore,
    loading: loadingOpenSeaCollectionNfts,
  } = useOpenSeaPaginatedNfts({ openSeaCollection });

  const { data: openSeaCollectionNft, loading: loadingOpenSeaCollectionNft } =
    useCollectionNftQuery({
      variables: { slug: openSeaCollection.slug, collectionNftId: searchToken },
      skip: !(openSeaCollection && searchToken),
    });

  const mappedNfts = useMemo<SimpleInfiniteListItem<CollectionNft>[]>(() => {
    if (searchToken !== "") {
      const nft = openSeaCollectionNft?.collectionNft;
      if (nft == null) return [];
      return [
        {
          key: `${nft.contract}-${nft.id}`,
          id: nft.id,
          title: nft.name,
          imageUrl: nft.imageUrl ?? "",
          data: nft,
        },
      ];
    }

    if (openSeaCollectionNfts == null) return [];

    return openSeaCollectionNfts.map((nft) => {
      return {
        key: `${nft.contract}-${nft.id}`,
        id: nft.id,
        title: nft.name,
        imageUrl: nft.imageUrl ?? "",
        data: nft,
      };
    });
  }, [openSeaCollectionNfts, openSeaCollectionNft?.collectionNft, searchToken]);

  return (
    <Box>
      <SectionFilterResults
        query={searchToken || ""}
        onChangeQuery={setSearchToken}
        isLoading={loadingOpenSeaCollectionNft}
      />

      <Space h={"xl"} />

      <ScrollArea.Autosize maxHeight={500}>
        <SimpleInfiniteGrid<CollectionNft>
          items={mappedNfts}
          onSelectItem={onSelectToken}
          hasMore={hasMore && searchToken === ""}
          loadMore={loadMore}
          loading={loadingOpenSeaCollectionNft || loadingOpenSeaCollectionNfts}
          selectedItem={selectedToken}
        />
      </ScrollArea.Autosize>
    </Box>
  );
}

function useOpenSeaPaginatedNfts(props: {
  openSeaCollection: CollectionDetailsQuery["collectionDetails"];
}) {
  const { openSeaCollection } = props;
  const [cursor, setCursor] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [openSeaCollectionNfts, setOpenSeaCollectionNfts] =
    useState<CollectionNfts>();

  const query = useCollectionNftsQuery({
    variables: { slug: openSeaCollection.slug, cursor },
    skip: !(openSeaCollection && openSeaCollection.standard),
    onCompleted(data) {
      if (data.collectionNftsPaginated.pageInfo.endCursor === cursor) return;
      const data2 = data.collectionNftsPaginated;
      setNextCursor(data2.pageInfo.endCursor);
      setOpenSeaCollectionNfts((collectionNfts) => {
        return (collectionNfts ?? []).concat(data2.nfts);
      });
    },
    fetchPolicy: "cache-and-network",
  });

  const loadMore = () => {
    setCursor(nextCursor);
  };

  const reset = () => {
    setCursor(null);
    setNextCursor(null);
  };

  const hasMore = nextCursor != null;

  return {
    ...query,
    openSeaCollectionNfts,
    hasMore,
    loadMore,
    reset,
  };
}
